
import { Centrifuge } from 'centrifuge'

class Event {
  name: string
  model: string | null
  action: ((nuxtApp: any, data: any) => void) | null

  constructor(name: string, { model, action }: { model?: string; action?: (nuxtApp: any, data: any) => void }) {
    this.name = name
    this.model = model || null
    this.action = action || null
  }

  emit(nuxtApp: any, data: any) {
    if (data && this.model) {
      nuxtApp.$bus.emit.created(this.model, data)
    }
  }
}

export const handleReservationCreated = (nuxtApp: any, data: any) => {
  const i18n = nuxtApp.$i18n
  const isAttendee = Boolean(data?.idReservationCustomer)

  if (isAttendee) {
    nuxtApp.$bus.emit.created('ReservationCustomer', data)
  } else if (data?.idReservation) {
    nuxtApp.$bus.emit.created('Reservation', data)
  }

  if (data?.event === 'reservation.created' && data.source?.type === 'customer') {
    nuxtApp.$notification.toast({
      title: i18n.t('plugins.centrifuge.booked', { name: data.source?.user?.name }),
      action: {
        label: i18n.t('general.view'),
        click: () => {
          if (isAttendee) {
            nuxtApp.$panel.create(`/appointment/attendee/${data.idReservationCustomer}`)
          } else if (data.idReservation) {
            nuxtApp.$panel.create(`/appointment/${data.idReservation}?date=${data.date}`)
          }
        },
      },
    })
  }
}

const eventTypes: Record<string, Event> = {
  'message-received': new Event('message-received', {
    model: 'CustomerMessage',
    action: (nuxtApp, data) => {
      const i18n = nuxtApp.$i18n

      if (useRuntimeConfig().public.instance === 'dev') {
        if (data && data.sender === 2) {
          nuxtApp.$notification.toast({
            title: i18n.t('plugins.centrifuge.messageFrom', { label: data.senderLabel }),
            text: data.title || data.body,
            action: {
              label: i18n.t('general.view'),
              click: () => {
                if (!useRoute().path.includes(`message/${data.idCustomer}`)) {
                  nuxtApp.$panel.create(`/message/chat/${data.idCustomer}`)
                }
              },
            },
          })
        }
      }
    },
  }),
  'message.updated': new Event('message.updated', {
    model: 'CustomerMessage',
    action: (nuxtApp, data) => {
      nuxtApp.$bus.emit.updated('CustomerMessage', data)
    },
  }),
  'account.updated': new Event('account.updated', {
    model: 'Location',
    action: (nuxtApp, data) => {
      nuxtApp.$auth.syncInfo()
    },
  }),
  'reservation.created': new Event('reservation.created', {
    action: handleReservationCreated,
  }),
}

export interface CentrifugePlugin {
  instance: Centrifuge | null
  channel: any
  connect(): Promise<void>
  disconnect(): void
}

export default defineNuxtPlugin((nuxtApp) => {
  const plugin: CentrifugePlugin = {
    instance: null,
    channel: null,

    async connect() {
      try {
        const $auth = nuxtApp.$auth as any
        const events = $auth?.info?.events

        if (events && events.enabled) {
          this.disconnect()

          this.instance = new Centrifuge(events.endpoints.websocket, {
            token: events.token,
            getToken: async () => {
              const info = await (nuxtApp.$auth as any).fetchInfo()
              return info?.events?.token
            },
          })

          this.instance.on('connected', function (ctx) {
            console.log(ctx)
          })

          this.instance.on('disconnected', function (ctx) {
            console.log(ctx)
          })

          this.instance.on('subscribing', function (_ctx) {
            // subscribing
          })

          this.instance.on('subscribed', function (_ctx) {
            // subscribed
          })

          this.instance.on('unsubscribed', function (_ctx) {
            // unsubscribed
          })

          this.instance.on('publication', function (ctx) {
            try {
              let eventType = eventTypes[ctx?.data?.event]

              if (!eventType) {
                const eventTypeKey = Object.keys(eventTypes).find(key => ctx?.data?.event?.startsWith(key))
                eventType = eventTypes[eventTypeKey!]
              }

              if (eventType) {
                const data = ctx?.data

                eventType.emit(nuxtApp, data)

                if (eventType.action) {
                  eventType.action(nuxtApp, data)
                }
              }
            } catch (e) {
              console.log(e)
            }
          })

          this.instance.on('error', function (ctx) {
            console.log(ctx)
          })

          await this.instance.connect()
        }
      } catch (e) {
        console.log(e)
      }
    },

    disconnect() {
      if (this.instance) {
        this.instance.disconnect()
        this.channel = null
      }
    },
  }

  nuxtApp.provide('centrifuge', plugin)
})
